import { useEffect, useState } from 'react';
import { getThemeDiscussionPalette } from '../../config/themes';
import { MarkdownPreview } from './NodeContent';

const MAX_RENDERED_MARKDOWN_CHARACTERS = 250_000;

function AttachmentViewerModal({
  attachment,
  content,
  objectUrl,
  isLoading,
  error,
  isDarkMode,
  theme,
  onClose,
  onDownload,
}) {
  const [markdownMode, setMarkdownMode] = useState('preview');
  const palette = getThemeDiscussionPalette(theme, isDarkMode);
  const isMarkdown = attachment?.kind === 'markdown';
  const markdownContent = typeof content === 'string' ? content : '';
  const previewWasTruncated = isMarkdown && markdownContent.length > MAX_RENDERED_MARKDOWN_CHARACTERS;
  const previewContent = previewWasTruncated
    ? markdownContent.slice(0, MAX_RENDERED_MARKDOWN_CHARACTERS)
    : markdownContent;

  useEffect(() => {
    setMarkdownMode('preview');
  }, [attachment?.assetId]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      className="attachment-viewer-backdrop"
      role="presentation"
      data-export-ignore="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className="attachment-viewer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="attachment-viewer-title"
        style={{
          background: palette.panel,
          color: palette.text,
          borderColor: palette.border,
        }}
        onMouseDown={(event) => event.stopPropagation()}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="attachment-viewer__header" style={{ borderColor: palette.border }}>
          <div id="attachment-viewer-title" className="attachment-viewer__title">
            {attachment?.filename || (isMarkdown ? 'Markdown file' : 'Image')}
          </div>
          {isMarkdown && !isLoading && !error && (
            <div
              className="attachment-viewer__mode-switch"
              role="group"
              aria-label="Markdown display mode"
              style={{ background: palette.raised }}
            >
              {['preview', 'markdown'].map((candidate) => (
                <button
                  key={candidate}
                  type="button"
                  aria-pressed={markdownMode === candidate}
                  onClick={() => setMarkdownMode(candidate)}
                  style={{
                    background: markdownMode === candidate ? palette.accent : 'transparent',
                    color: markdownMode === candidate ? palette.accentText : palette.text,
                  }}
                >
                  {candidate === 'preview' ? 'Preview' : 'Markdown'}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="attachment-viewer__close"
            aria-label="Close attachment"
            onClick={onClose}
            style={{ color: palette.text }}
          >
            ×
          </button>
        </header>

        <div className="attachment-viewer__body">
          {isLoading ? (
            <div role="status" className="attachment-viewer__message">Loading attachment…</div>
          ) : error ? (
            <div role="alert" className="attachment-viewer__message">{error}</div>
          ) : isMarkdown ? (
            markdownMode === 'preview' ? (
              <article className="attachment-viewer__markdown-preview">
                {previewWasTruncated && (
                  <p className="attachment-viewer__notice" role="status">
                    This file is large, so the rendered preview is shortened. Switch to Markdown to view all of it.
                  </p>
                )}
                <MarkdownPreview source={previewContent || ''} />
              </article>
            ) : (
              <pre className="attachment-viewer__markdown-source" style={{ background: palette.input }}>
                <code>{markdownContent}</code>
              </pre>
            )
          ) : objectUrl ? (
            <img
              className="attachment-viewer__image"
              src={objectUrl}
              alt={attachment?.filename || 'Attached image'}
            />
          ) : null}
        </div>

        <footer className="attachment-viewer__footer" style={{ borderColor: palette.border }}>
          <span>{isMarkdown ? 'Markdown preview is read only for now.' : 'Full image preview'}</span>
          <button
            type="button"
            onClick={onDownload}
            disabled={isLoading || Boolean(error)}
            style={{ background: palette.accent, color: palette.accentText }}
          >
            Download
          </button>
        </footer>
      </section>
    </div>
  );
}

export default AttachmentViewerModal;
